import { r as registerInstance, h, H as Host, g as getElement } from './index-662ec80e.js';
import { f as findScrollableParent } from './position-997d909e.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { a1 as vUndefOr, a as vString, S as vOneOf, Q as vBoolean } from './validation-02b8d01d.js';
import { t as tuple } from './types-6f6b2fba.js';
import './i18n-939e2dee.js';

const tooltipSizes = tuple('xs', 'sm', 'md');
const themes = tuple('dark', 'light');

const dsTooltipCss = "ds-tooltip>.ds-tooltip{position:fixed;max-width:calc(100vw - 10px)}.ds-tooltip{position:absolute;visibility:hidden;opacity:0;pointer-events:none;transition:0.1s;will-change:transform;min-width:3.5rem;max-width:27rem;width:max-content;border-radius:var(--radius);background-color:var(--black);color:var(--white);white-space:pre-line;text-align:center;box-shadow:var(--shadow-1)}.ds-tooltip[data-theme='light']{background-color:var(--white);color:var(--coolGrey-700)}.ds-tooltip[data-size='xs']>*{font-size:0.79012346rem;line-height:1.3rem;padding:calc(var(--gw) * 0.2) calc(var(--gw) * 0.6)}.ds-tooltip[data-size='sm']>*{font-size:0.88888889rem;line-height:1.56rem;padding:calc(var(--gw) * 0.2) calc(var(--gw) * 0.6)}.ds-tooltip[data-size='md']>*{font-size:1rem;line-height:1.69rem;padding:calc(var(--gw) * 0.6) calc(var(--gw) * 1.2)}.overlay-container{position:fixed;z-index:99999}.overlay-container:empty{display:none}.overlay-container,.overlay-global-wrapper{pointer-events:none;top:0;left:0;height:100%;width:100%}.overlay-container .overlay-bounding-box,.overlay-global-wrapper .overlay-bounding-box{position:absolute;inset:0px;z-index:1000;display:-ms-flexbox;display:flex;-ms-flex-direction:column;flex-direction:column;min-width:1px;min-height:1px}.overlay-container .overlay-bounding-box .ds-tooltip,.overlay-global-wrapper .overlay-bounding-box .ds-tooltip{position:absolute;visibility:visible;opacity:1;z-index:9999;max-width:calc(100vw - 10px)}";

const DsTooltip = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.gutter = 5;
    this.propError = {};
    this.text = undefined;
    this.size = 'sm';
    this.isVisibleOnHover = true;
    this.isVisible = false;
    this.theme = 'dark';
    this.hasHtmlContent = false;
    this.positionY = 0;
    this.positionX = 0;
  }
  watchText() {
    /* istanbul ignore if */
    if (!propCheck(this, 'text', 'string', vUndefOr(vString)))
      return;
  }
  watchSize() {
    /* istanbul ignore if */
    if (!propCheck(this, 'size', tooltipSizes.join(' | '), vOneOf(tooltipSizes)))
      return;
  }
  watchIsVisibleOnHover() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isVisibleOnHover', 'boolean', vBoolean))
      return;
  }
  watchIsVisible() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isVisible', 'boolean', vBoolean))
      return;
    /* istanbul ignore else */
    if (!this.tooltipElement)
      return; // not yet rendered
    if (this.isVisible && (this.text || this.hasHtmlContent)) {
      this.attach();
    }
    else {
      this.detach();
    }
  }
  watchTheme() {
    /* istanbul ignore if */
    if (!propCheck(this, 'theme', themes.join(' | '), vOneOf(themes)))
      return;
  }
  watchHasHtmlContent() {
    /* istanbul ignore if */
    if (!propCheck(this, 'hasHtmlContent', 'boolean', vBoolean))
      return;
  }
  connectedCallback() {
    runWatchers(this);
  }
  componentDidRender() {
    this.createOverlay();
  }
  onMouseEnter() {
    /* istanbul ignore else */
    if (!this.isVisible && this.isVisibleOnHover && (this.text || this.hasHtmlContent)) {
      this.attach();
    }
  }
  onMouseLeave() {
    /* istanbul ignore else */
    if (!this.isVisible) {
      this.detach();
    }
  }
  componentDidLoad() {
    this.scrollingParent = findScrollableParent(this.host);
    this.resizeObserver = new ResizeObserver(() => this.getDimensions());
    this.resizeObserver.observe(this.host);
    this.resizeObserver.observe(this.scrollingParent);
    this.positionObserver = new MutationObserver(() => this.getDimensions());
    this.positionObserver.observe(this.host, { attributes: true });
    this.positionObserver.observe(this.scrollingParent, { attributes: true });
    if (this.isVisible)
      this.attach();
  }
  getDimensions() {
    if (!this.tooltipElement)
      return; // something went wrong
    this.tooltipRect = this.tooltipElement.getBoundingClientRect();
    this.hostRect = this.host.getBoundingClientRect();
    this.positionY = this.hostRect.top;
    this.positionX = this.hostRect.left;
    /** Position bottom */
    this.positionY = this.positionY + this.hostRect.height + this.gutter;
    /** Position top if no space */
    /* istanbul ignore else */
    if (this.positionY + this.tooltipRect.height > document.body.clientHeight) {
      this.positionY = this.positionY - this.hostRect.height - this.tooltipRect.height - 2 * this.gutter;
    }
    /** Position left if no space */
    if (this.positionX + this.tooltipRect.width > document.body.clientWidth) {
      this.positionX = this.positionX - this.tooltipRect.width;
    }
    /** If tooltip is too wide to fit left or right */
    if (this.positionX < 0) {
      this.positionX = this.gutter;
    }
  }
  render() {
    if (hasPropError(this))
      return displayPropError(this);
    return (h(Host, null, h("div", { class: "ds-tooltip", "data-theme": this.theme, "data-size": this.size, ref: (el) => (this.tooltipElement = el), style: {
        left: this.positionX + 'px',
        top: this.positionY + 'px',
      } }, h("div", { class: "text", style: {
        display: !this.hasHtmlContent ? 'block' : 'none',
      } }, this.text), h("div", { class: "html-content", style: {
        display: this.hasHtmlContent ? 'block' : 'none',
      } }, h("slot", { name: "htmlContent" }))), h("slot", null)));
  }
  disconnectedCallback() {
    /* istanbul ignore else */
    if (this.resizeObserver)
      this.resizeObserver.disconnect();
    /* istanbul ignore else */
    if (this.positionObserver)
      this.positionObserver.disconnect();
  }
  createOverlay() {
    const existingOverlay = document.querySelector('.overlay-container');
    if (existingOverlay) {
      this.overlay = existingOverlay;
      return;
    }
    const overlay = document.createElement('div');
    overlay.classList.add('overlay-container');
    document.body.appendChild(overlay);
    this.overlay = overlay;
  }
  attach() {
    this.detach();
    this.getDimensions();
    const bounding = document.createElement('div');
    bounding.classList.add('overlay-bounding-box');
    bounding.appendChild(this.tooltipElement);
    this.overlay.appendChild(bounding);
  }
  detach() {
    const existingBounding = document.querySelector('.overlay-bounding-box');
    if (existingBounding) {
      /** Returning tooltip back to host so to not break calculations */
      this.host.appendChild(this.tooltipElement);
      existingBounding.parentNode.removeChild(existingBounding);
    }
  }
  get host() { return getElement(this); }
  static get watchers() { return {
    "text": ["watchText"],
    "size": ["watchSize"],
    "isVisibleOnHover": ["watchIsVisibleOnHover"],
    "isVisible": ["watchIsVisible"],
    "theme": ["watchTheme"],
    "hasHtmlContent": ["watchHasHtmlContent"]
  }; }
};
DsTooltip.style = dsTooltipCss;

export { DsTooltip as ds_tooltip };
